/* eslint-disable @typescript-eslint/no-explicit-any */
import { StartChatPopup } from '@cian/start-chat-popup-component';
import { DescriptionFormatter, StartChatWidget, TitleFormatter } from '@cian/start-chat-widget';
import { IStyleArgument, mergeStyles } from '@cian/utils/lib/shared/style';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import * as Modal from 'react-modal';
import { connect } from 'react-redux';
import { sendFirstMessage } from '../../api/chats';
import { IAgentAvailabilitySchema, IOfferSchema, IUserSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { IContext, IState } from '../../store';
import { sendSystemPushNotificationPopup } from '../../utils/flocktory';
import * as styles from './index.css';
import { DEFAULT_CHAT_MESSAGE } from '../../constants/text';
import { IMessagesOfferRequest, IMessagesOfferResponse } from '../../repositories/chats/v1/messages-offer';

export interface ICallbackStoreProps {
  offer: IOfferSchema;
  user: IUserSchema | null;
  agentAvailability: IAgentAvailabilitySchema | null;
  pushSubscribed: boolean;
  captchaSpamSiteKey: string;
}

export interface ICallbackOwnProps {
  isOpen: boolean;
  message?: string;
  isShowInDropdown: boolean | null;
  dropdownCustomClass?: IStyleArgument;
  onMessageChange(value: string): void;
  onMessageSent(chatId?: string): void;
  onClose(): void;
}

export type IStartChatProps = ICallbackStoreProps & ICallbackOwnProps;

class StartChat extends React.Component<IStartChatProps, {}> {
  public context: IContext;

  public state = {
    isCanRender: false,
  };

  public componentDidMount() {
    this.setState({
      isCanRender: true,
    });
  }

  public static contextTypes = {
    chatsHttpConfig: PropTypes.object,
    httpApi: PropTypes.object,
  };

  public concatenationPhones(offer: IOfferSchema) {
    return offer && offer.phones && offer.phones.length > 0
      ? offer.phones.map(p => `${p.countryCode}${p.number}`)
      : undefined;
  }

  public getUserPhone(user: IUserSchema | null) {
    return user && user.phones && user.phones.length > 0 ? user.phones.find(p => !!p.phone) : undefined;
  }

  public renderInPopup() {
    const {
      message,
      onMessageChange,
      onMessageSent,
      isOpen,
      onClose,
      offer,
      user,
      agentAvailability,
      captchaSpamSiteKey,
      pushSubscribed,
    } = this.props;
    const phone = this.getUserPhone(user);
    const phones = this.concatenationPhones(offer);

    const isNotAvailable = !agentAvailability || !agentAvailability.available;

    return (
      <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        onRequestClose={onClose}
        style={{
          content: {
            overflow: 'visible',
            padding: '0',
            position: 'static',
            width: '500px',
            zIndex: 101,
          },
          overlay: {
            alignItems: 'center',
            backgroundColor: 'rgba(24, 41, 48, .8)',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 100,
          },
        }}
      >
        <StartChatWidget
          offerId={offer.id}
          userEmail={(user && user.email) || undefined}
          userPhone={(phone && phone.phone) || undefined}
          onClose={onClose}
          initialMessage={message || DEFAULT_CHAT_MESSAGE}
          onMessageChanged={onMessageChange}
          onMessageSent={onMessageSent}
          sendFirstMessage={this.sendFirstMessage}
          customTitle={agentAvailability && isNotAvailable ? renderCustomChatTitle(agentAvailability) : null}
          customDescription={
            agentAvailability && isNotAvailable ? renderCustomChatDescription(agentAvailability, phones || []) : null
          }
          showPushCheckbox={pushSubscribed}
          onFlocktoryCallback={this.callSystemPushNotificationPopup}
          sitekey={captchaSpamSiteKey}
        />
      </Modal>
    );
  }

  public renderInDropdown() {
    const {
      isOpen,
      offer,
      user,
      agentAvailability,
      onMessageSent,
      dropdownCustomClass,
      captchaSpamSiteKey,
      pushSubscribed,
    } = this.props;
    const isOwn = offer.cianUserId === (user && user.realtyUserId);
    const phones = this.concatenationPhones(offer);
    let userPhone = Object.assign({}, this.getUserPhone(user));

    if (userPhone && userPhone.phone) {
      userPhone.phone = userPhone.phone[0] === '+' ? userPhone.phone : `+${userPhone.phone}`;
    }

    return (
      <div {...mergeStyles([styles['dropdown'], isOpen && styles['show'], dropdownCustomClass])}>
        <StartChatPopup
          headerProps={{
            agentAvailability,
            phones,
          }}
          defaultPhone={userPhone && userPhone.phone ? userPhone.phone : ''}
          defaultEmail={user && user.email ? user.email : ''}
          defaultMessage={DEFAULT_CHAT_MESSAGE}
          error={isOwn ? 'Вы не можете написать сообщение самому себе' : undefined}
          offerId={offer.id}
          onMessageSent={() => {
            return onMessageSent();
          }}
          // https://cianru.atlassian.net/browse/CD-50479
          //tslint:disable-next-line:no-any
          sendFirstMessage={this.sendFirstMessage as any}
          isShowPushCheckbox={pushSubscribed}
          onFlocktoryCallback={this.callSystemPushNotificationPopup}
          sitekey={captchaSpamSiteKey}
        />
      </div>
    );
  }

  public render() {
    const { isCanRender } = this.state;
    const { isShowInDropdown } = this.props;

    return isShowInDropdown && isCanRender ? this.renderInDropdown() : this.renderInPopup();
  }

  private sendFirstMessage = (params: IMessagesOfferRequest): Promise<IMessagesOfferResponse> => {
    return sendFirstMessage(this.context.httpApi, {
      captchaToken: params.captchaToken,
      email: params.email,
      offerId: params.offerId,
      phone: params.phone,
      text: params.text,
    });
  };

  private callSystemPushNotificationPopup = (isPushCheck: boolean) => {
    const { pushSubscribed } = this.props;

    sendSystemPushNotificationPopup(pushSubscribed, isPushCheck);
  };
}

function renderCustomChatTitle({ availableFrom, availableTo, vacationTo }: IAgentAvailabilitySchema) {
  if ((availableFrom && availableTo) || vacationTo) {
    return <TitleFormatter callFrom={availableFrom} callTo={availableTo} vacation={vacationTo} />;
  }

  return null;
}

function renderCustomChatDescription({ availableFrom, vacationTo }: IAgentAvailabilitySchema, phones: string[]) {
  return (
    <DescriptionFormatter
      callAt={vacationTo || availableFrom || ''}
      isVacation={!!vacationTo}
      additions={phones.map((p, i) => (
        <span key={p + i}>
          <a className={styles['phone']} href={`tel:${p}`}>
            {p}
          </a>
          {i !== phones.length - 1 ? ', ' : ''}
        </span>
      ))}
    />
  );
}

function mapStateToProps(state: IState): ICallbackStoreProps {
  return {
    agentAvailability: state.offerData.agentAvailability,
    captchaSpamSiteKey: state.captchaSpamSiteKey,
    offer: state.offerData.offer,
    pushSubscribed: state.pushSubscribed,
    user: state.offerData.user,
  };
}

export const StartChatComponent = connect<ICallbackStoreProps, {}, ICallbackOwnProps>(mapStateToProps)(StartChat);
